import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { cn } from "@/lib/utils";
import logo from "@/assets/FounderMitra_logo_trimmed.webp";
import { useHeaderTheme } from "./HeaderThemeContext";

const navigation = [
  { name: "Services", href: "/services" },
  { name: "How We Think", href: "/how-we-think" },
  { name: "How We Work", href: "/how-we-work" },
  { name: "Industries", href: "/industries" },
  { name: "Engagements", href: "/strategic-engagements" },
  { name: "About", href: "/about" },
  { name: "Insights", href: "/insights" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const location = useLocation();
  const { theme } = useHeaderTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const footer = document.getElementById("site-footer");
    if (!footer) {
      setNearFooter(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setNearFooter(entry.isIntersecting),
      { rootMargin: "200px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, [location.pathname]);

  // Close mobile menu on Escape (WCAG 2.1.2 - no keyboard trap)
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen]);

  // When the page declares a light hero, render the dark-on-light header until
  // the user scrolls past the hero. Otherwise stay on the white-on-dark variant.
  const isLight = theme === "light" && !scrolled;

  const logoClass = isLight ? "" : "brightness-0 invert";
  const pillClass = isLight
    ? "bg-white/70 backdrop-blur-xl border border-primary/10 shadow-[0_8px_30px_-12px_hsl(248_40%_10%/0.18)]"
    : "bg-white/[0.07] backdrop-blur-xl border border-white/15 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.5)]";
  const linkBase = isLight
    ? "text-foreground/70 hover:text-primary hover:bg-primary/5"
    : "text-white/80 hover:text-white hover:bg-white/10";
  const linkActive = isLight ? "text-primary bg-primary/10" : "text-white bg-white/15";
  const ctaClass = isLight
    ? "bg-[hsl(248_62%_21%)] text-white hover:bg-primary/90 border border-primary/20 shadow-[0_10px_30px_-12px_hsl(248_62%_21%/0.45)]"
    : "bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white shadow-[0_8px_30px_-10px_rgba(0,0,0,0.5)]";
  const mobileBtnClass = isLight
    ? "text-primary bg-white/70 hover:bg-white border border-primary/10 backdrop-blur-md"
    : "text-white bg-white/10 hover:bg-white/20 border border-white/15 backdrop-blur-md";

  const LogoMark = (
    <Link to="/" className="group relative inline-block">
      <span className="sr-only">Founder Mitra</span>
      <motion.img
        layoutId="fm-brand-logo"
        src={logo}
        alt="Founder Mitra"
        transition={{ type: "spring", stiffness: 220, damping: 26, mass: 0.9 }}
        className={cn(
          "w-auto origin-left group-hover:scale-105",
          "h-8 sm:h-9 lg:h-10",
          logoClass,
          isLight ? "drop-shadow-none" : "drop-shadow-[0_4px_20px_rgba(0,0,0,0.35)]"
        )}
      />
    </Link>
  );

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <LayoutGroup>
      <nav className="container mx-auto px-6 sm:px-10 lg:px-16" aria-label="Global">
        <div className="flex h-auto py-5 md:py-6 lg:py-7 items-center justify-between">
          <motion.div
            className="flex lg:flex-1 min-w-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            {!scrolled && LogoMark}
          </motion.div>

          <div className="flex lg:hidden">
            <motion.button
              type="button"
              className={cn("relative inline-flex items-center justify-center rounded-full p-3 transition-colors", mobileBtnClass)}
              onClick={() => setMobileMenuOpen(true)}
              whileTap={{ scale: 0.95 }}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-haspopup="dialog"
            >
              <span className="sr-only">Open main menu</span>
              <Menu className="h-5 w-5" aria-hidden="true" />
            </motion.button>
          </div>

          <AnimatePresence>
            {!scrolled && (
              <motion.div
                key="nav-pill"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className={cn("hidden lg:flex lg:items-center lg:gap-x-1 rounded-full px-2 py-1.5", pillClass)}
              >
                <ul role="list" className="flex items-center gap-x-1 m-0 p-0 list-none">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        aria-current={location.pathname === item.href ? "page" : undefined}
                        className={cn(
                          "relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300",
                          location.pathname === item.href ? linkActive : linkBase
                        )}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center min-w-0 relative"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            {/* CTA stays mounted to reserve the slot; fades out on scroll */}
            <motion.div
              animate={{ opacity: scrolled ? 0 : 1 }}
              transition={{ duration: 0.25 }}
              className={scrolled ? "pointer-events-none" : ""}
            >
              <Button asChild className={cn("group relative overflow-hidden rounded-full px-6 h-11", ctaClass)}>
                <Link to="/contact" className="flex items-center gap-2">
                  Get in Touch
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
            {/* Logo lands here, exactly over the CTA slot */}
            {scrolled && (
              <motion.div
                className="absolute inset-y-0 right-0 flex items-center"
                animate={{ opacity: nearFooter ? 0 : 1 }}
                transition={{ duration: 0.3 }}
                style={{ pointerEvents: nearFooter ? "none" : "auto" }}
              >
                {LogoMark}
              </motion.div>
            )}
          </motion.div>
        </div>
      </nav>
      </LayoutGroup>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              id="mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-[#0b0b0b] text-white shadow-2xl lg:hidden border-l border-white/10"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                  <img src={logo} alt="Founder Mitra" width={160} height={32} decoding="async" className="h-8 w-auto brightness-0 invert" />
                </Link>
                <motion.button
                  type="button"
                  className="rounded-full p-2.5 min-h-11 min-w-11 inline-flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="sr-only">Close menu</span>
                  <X className="h-5 w-5" aria-hidden="true" />
                </motion.button>
              </div>

              <div className="p-6">
                <ul role="list" className="space-y-1 m-0 p-0 list-none">
                  {navigation.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                    >
                      <Link
                        to={item.href}
                        aria-current={location.pathname === item.href ? "page" : undefined}
                        className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                          location.pathname === item.href
                            ? "text-white bg-white/15"
                            : "text-white/80 hover:bg-white/10 hover:text-white"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>

                <motion.div
                  className="mt-8 pt-6 border-t border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button asChild className="w-full rounded-full bg-white text-[hsl(248_62%_21%)] hover:bg-white/90 shadow-lg">
                    <Link to="/book-call" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center gap-2">
                      Book a Call
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
